import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useAlertModal } from '../../AlertModalContext';
import { useUserContext } from '../../UserContext';

/* ── ícones reutilizáveis ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2 }) => (
    <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
    </svg>
);

const ICONS = {
    plus:    'M12 4v16m8-8H4',
    edit:    'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    trash:   'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
    info:    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    check:   'M5 13l4 4L19 7',
    close:   'M6 18L18 6M6 6l12 12',
    shield:  'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    user:    'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
};

/* ── badge de tipo de perfil ── */
const TipoBadge = ({ tipo }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: '700',
        textTransform: 'uppercase', letterSpacing: '0.5px',
        backgroundColor: tipo === 'padrao' ? '#f1f5f9' : '#eff6ff',
        color:           tipo === 'padrao' ? '#64748b'  : '#2563eb',
        border: `1px solid ${tipo === 'padrao' ? '#e2e8f0' : '#bfdbfe'}`,
    }}>
        {tipo === 'padrao' ? 'Padrão' : 'Customizado'}
    </span>
);

/* ── célula de acesso (✔ / ✘) ── */
const AccessCell = ({ hasAccess }) => (
    <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '24px', height: '24px', borderRadius: '50%',
        backgroundColor: hasAccess ? '#f0fdf4' : '#fef2f2',
        border: `1px solid ${hasAccess ? '#bbf7d0' : '#fecaca'}`,
    }}>
        <Icon
            d={hasAccess ? ICONS.check : ICONS.close}
            size={12}
            color={hasAccess ? '#16a34a' : '#dc2626'}
            stroke={2.5}
        />
    </div>
);

/* ════════════════════════════════════════════════════════ */

const PerfisTab = ({ currentEntidade }) => {
    const [perfis, setPerfis]               = useState([]);
    const [isLoading, setIsLoading]         = useState(false);
    const [showModal, setShowModal]         = useState(false);
    const [isEditing, setIsEditing]         = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [perfilParaExcluir, setPerfilParaExcluir] = useState(null);

    const [currentPerfil, setCurrentPerfil] = useState({
        id: null, nome: '', descricao: '', tipo: 'customizado', entidade_id: null,
        permissoes: { modulos: [], abas: {} },
    });

    const { showAlertModal }                         = useAlertModal();
    const { dynamicAccessConfig, isLoadingAccessConfig } = useUserContext();
    const fetchedPerfisForEntityId                   = useRef(null);

    /* ── fetch ── */
    const fetchPerfis = useCallback(async () => {
        if (!currentEntidade?.id) return;
        if (fetchedPerfisForEntityId.current === currentEntidade.id) return;
        setIsLoading(true);
        try {
            const response = await axios.get(`/api/perfis?entidade_id=${currentEntidade.id}`);
            setPerfis(response.data.perfis || []);
            fetchedPerfisForEntityId.current = currentEntidade.id;
        } catch (error) {
            showAlertModal('Erro', 'Não foi possível carregar os perfis.', 'danger');
            setPerfis([
                { id: 'std1', nome: 'Representante Legal', descricao: 'Acesso total ao sistema.', tipo: 'padrao', entidade_id: null },
                { id: 'std2', nome: 'Médico',              descricao: 'Acesso ao consultório e prontuários.', tipo: 'padrao', entidade_id: null },
                { id: 'std3', nome: 'Recepção',            descricao: 'Agenda, pacientes e financeiro básico.', tipo: 'padrao', entidade_id: null },
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade?.id, showAlertModal]);

    useEffect(() => { fetchPerfis(); }, [fetchPerfis]);

    /* ── modal abrir/fechar ── */
    const emptyPerfil = useCallback(() => ({
        id: null, nome: '', descricao: '', tipo: 'customizado',
        entidade_id: currentEntidade?.id,
        permissoes: { modulos: [], abas: {} },
    }), [currentEntidade?.id]);

    const handleOpenModal = (perfil = null) => {
        if (perfil) {
            if (perfil.tipo === 'padrao') {
                showAlertModal('Atenção', 'Perfis padrão não podem ser editados.', 'warning');
                return;
            }
            setIsEditing(true);
            setCurrentPerfil({
                ...perfil,
                permissoes: perfil.permissoes || { modulos: [], abas: {} },
                entidade_id: currentEntidade?.id,
            });
        } else {
            setIsEditing(false);
            setCurrentPerfil(emptyPerfil());
        }
        setShowModal(true);
    };

    const handleCloseModal = () => { setShowModal(false); setCurrentPerfil(emptyPerfil()); };

    /* ── submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentPerfil.nome.trim()) { showAlertModal('Validação', 'O nome do perfil é obrigatório.', 'warning'); return; }
        if (!currentEntidade)           { showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger'); return; }
        setIsLoading(true);
        const payload = { ...currentPerfil, entidade_id: currentEntidade.id };
        try {
            if (isEditing) {
                await axios.put(`/api/perfis/${currentPerfil.id}`, payload);
                showAlertModal('Sucesso', 'Perfil atualizado com sucesso!', 'success');
            } else {
                await axios.post('/api/perfis', payload);
                showAlertModal('Sucesso', 'Perfil criado com sucesso!', 'success');
            }
            fetchedPerfisForEntityId.current = null; // força re-fetch
            fetchPerfis();
            handleCloseModal();
        } catch (error) {
            showAlertModal('Erro', `Não foi possível salvar: ${error.response?.data?.message || error.message}`, 'danger');
        } finally { setIsLoading(false); }
    };

    /* ── delete ── */
    const handleOpenDeleteModal = (perfil) => { setPerfilParaExcluir(perfil); setShowDeleteModal(true); };
    const handleCloseDeleteModal = () => { setShowDeleteModal(false); setPerfilParaExcluir(null); };

    const handleConfirmDelete = async () => {
        if (!perfilParaExcluir) return;
        setIsLoading(true);
        try {
            await axios.delete(`/api/perfis/${perfilParaExcluir.id}`);
            showAlertModal('Sucesso', 'Perfil excluído com sucesso!', 'success');
            fetchedPerfisForEntityId.current = null;
            fetchPerfis();
        } catch (error) {
            showAlertModal('Erro', `Não foi possível excluir: ${error.response?.data?.message || error.message}`, 'danger');
        } finally { setIsLoading(false); handleCloseDeleteModal(); }
    };

    /* ── permissões ── */
    const handlePermissaoModuloChange = (moduleKey, isChecked) => {
        setCurrentPerfil(prev => {
            const newModulos = isChecked
                ? [...(prev.permissoes.modulos || []), moduleKey]
                : (prev.permissoes.modulos || []).filter(k => k !== moduleKey);
            const newAbas = { ...(prev.permissoes.abas || {}) };
            if (!isChecked) delete newAbas[moduleKey];
            return { ...prev, permissoes: { ...prev.permissoes, modulos: newModulos, abas: newAbas } };
        });
    };

    const handlePermissaoAbaChange = (moduleKey, tabKey, isChecked) => {
        setCurrentPerfil(prev => {
            const abasDoModulo    = prev.permissoes.abas?.[moduleKey] || [];
            const newAbasDoModulo = isChecked ? [...abasDoModulo, tabKey] : abasDoModulo.filter(k => k !== tabKey);
            const newAbas = { ...(prev.permissoes.abas || {}), [moduleKey]: newAbasDoModulo };
            if (newAbasDoModulo.length === 0) delete newAbas[moduleKey];
            return { ...prev, permissoes: { ...prev.permissoes, abas: newAbas } };
        });
    };

    /* ── structuredPermissions ── */
    const structuredPermissions = useMemo(() =>
        Object.entries(dynamicAccessConfig)
            .map(([moduleKey, moduleConfig]) => {
                if (!moduleConfig?.title) return null;
                const tabs = Object.entries(moduleConfig)
                    .map(([itemKey, itemConfig]) => {
                        if (['header','key','title'].includes(itemKey)) return null;
                        if (itemConfig && typeof itemConfig === 'object' && itemConfig.key && itemConfig.title)
                            return { tabKey: itemConfig.key, tabTitle: itemConfig.title };
                        return null;
                    }).filter(Boolean);
                return { moduleKey: moduleConfig.key || moduleKey, moduleTitle: moduleConfig.title, tabs };
            }).filter(Boolean),
    [dynamicAccessConfig]);

    /* ── checkAccess ── */
    const checkAccess = (perfil, moduleKey, tabKey = null) => {
        if (!perfil || !moduleKey) return false;
        if (perfil.tipo === 'padrao') {
            const mc = dynamicAccessConfig[moduleKey];
            if (!mc) return false;
            const hasModule = mc.header?.roles?.includes(perfil.nome) || false;
            if (!tabKey) return hasModule;
            if (!hasModule) return false;
            const tc = Object.values(mc).find(i => i && i.key === tabKey);
            return tc?.roles?.includes(perfil.nome) || false;
        }
        if (perfil.tipo === 'customizado') {
            const hasModule = perfil.permissoes?.modulos?.includes(moduleKey) || false;
            if (!tabKey) return hasModule;
            if (!hasModule) return false;
            return perfil.permissoes?.abas?.[moduleKey]?.includes(tabKey) || false;
        }
        return false;
    };

    /* ── loading state ── */
    if ((isLoading && perfis.length === 0) || isLoadingAccessConfig) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '12px', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
                <Spinner animation="border" variant="primary" style={{ width: '32px', height: '32px' }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Carregando perfis...</p>
            </div>
        );
    }

    /* ════════════════════ RENDER ════════════════════ */
    return (
        <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                .perfis-matrix-table { width: 100%; border-collapse: collapse; }
                .perfis-matrix-table th, .perfis-matrix-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; }
                .perfis-matrix-table thead tr { background: linear-gradient(135deg, #f8fafc, #f1f5f9); }
                .perfis-matrix-table tbody tr:hover { background: #fafbff; }
                .perfis-matrix-table .module-row { background: #f8fafc; }
                .perfis-matrix-table .module-row td { border-top: 2px solid #e2e8f0; }
                .perm-checkbox { accent-color: #2563eb; width: 15px; height: 15px; cursor: pointer; }
                .perm-checkbox:disabled { accent-color: #d1d5db; cursor: not-allowed; }
            `}</style>

            {/* ── CABEÇALHO ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <div>
                    <h4 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#111827' }}>
                        Matriz de Permissões por Perfil
                    </h4>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Gerencie os acessos de cada perfil aos módulos e abas do sistema
                    </p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '7px',
                        padding: '9px 18px', borderRadius: '8px', border: 'none',
                        background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                        color: '#fff', fontSize: '13px', fontWeight: '600',
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                        fontFamily: 'inherit',
                    }}
                >
                    <Icon d={ICONS.plus} size={14} color="#fff" />
                    Novo Perfil Customizado
                </button>
            </div>

            {/* ── CARD INFORMATIVO ── */}
            <div style={{
                backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: '12px', padding: '18px 20px', marginBottom: '24px',
                display: 'flex', gap: '14px',
            }}>
                <div style={{ flexShrink: 0, marginTop: '2px' }}>
                    <Icon d={ICONS.info} size={18} color="#2563eb" />
                </div>
                <div>
                    <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#1e40af' }}>
                        Entendendo os Perfis Padrão
                    </p>
                    <ul style={{ margin: '0 0 10px', paddingLeft: '18px', fontSize: '13px', color: '#374151', lineHeight: '1.7' }}>
                        <li><strong>Representante Legal:</strong> Acesso total, incluindo configurações e gestão de perfis.</li>
                        <li><strong>Médico:</strong> Focado no consultório — prontuários, agendamentos próprios e atendimento.</li>
                        <li><strong>Recepção:</strong> Agenda de todos os médicos, check-in de pacientes e registro de pagamentos.</li>
                    </ul>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                        Para o sistema funcionar em capacidade máxima, é ideal ter ao menos um colaborador com o perfil de <strong>Recepção</strong>.
                    </p>
                </div>
            </div>

            {/* ── MATRIZ DE PERMISSÕES ── */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {/* Cabeçalho da tabela */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
                    background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon d={ICONS.shield} size={16} color="#2563eb" />
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                            Permissões por Módulo e Aba
                        </span>
                    </div>
                    <span style={{
                        fontSize: '11px', fontWeight: '700', color: '#2563eb',
                        backgroundColor: '#eff6ff', padding: '3px 10px',
                        borderRadius: '999px', border: '1px solid #bfdbfe',
                    }}>
                        {perfis.length} {perfis.length === 1 ? 'perfil' : 'perfis'}
                    </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table className="perfis-matrix-table">
                        <thead>
                            <tr>
                                <th style={{ minWidth: '240px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Permissão (Módulo / Aba)
                                </th>
                                {perfis.map(perfil => (
                                    <th key={perfil.id} style={{ textAlign: 'center', minWidth: '130px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                            {/* Avatar */}
                                            <div style={{
                                                width: '32px', height: '32px', borderRadius: '50%',
                                                background: perfil.tipo === 'padrao'
                                                    ? 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
                                                    : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <Icon d={ICONS.user} size={14}
                                                    color={perfil.tipo === 'padrao' ? '#64748b' : '#2563eb'} />
                                            </div>
                                            {/* Nome */}
                                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#111827', whiteSpace: 'nowrap' }}>
                                                {perfil.nome}
                                            </span>
                                            <TipoBadge tipo={perfil.tipo} />
                                            {/* Ações customizados */}
                                            {perfil.tipo === 'customizado' && (
                                                <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                                                    <button
                                                        onClick={() => handleOpenModal(perfil)}
                                                        title="Editar perfil"
                                                        style={{
                                                            width: '26px', height: '26px', borderRadius: '6px',
                                                            border: '1px solid #fde68a', backgroundColor: '#fffbeb',
                                                            color: '#d97706', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Icon d={ICONS.edit} size={12} color="#d97706" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(perfil)}
                                                        title="Excluir perfil"
                                                        style={{
                                                            width: '26px', height: '26px', borderRadius: '6px',
                                                            border: '1px solid #fecaca', backgroundColor: '#fef2f2',
                                                            color: '#dc2626', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Icon d={ICONS.trash} size={12} color="#dc2626" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {structuredPermissions.map(modulePerm => (
                                <React.Fragment key={modulePerm.moduleKey}>
                                    {/* Linha de módulo */}
                                    <tr className="module-row">
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{
                                                    width: '6px', height: '6px', borderRadius: '50%',
                                                    backgroundColor: '#2563eb', flexShrink: 0,
                                                }} />
                                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>
                                                    {modulePerm.moduleTitle}
                                                </span>
                                                <span style={{
                                                    fontSize: '10px', color: '#9ca3af',
                                                    backgroundColor: '#f1f5f9', padding: '1px 6px',
                                                    borderRadius: '4px',
                                                }}>módulo</span>
                                            </div>
                                        </td>
                                        {perfis.map(perfil => (
                                            <td key={`${modulePerm.moduleKey}-${perfil.id}`} style={{ textAlign: 'center' }}>
                                                <AccessCell hasAccess={checkAccess(perfil, modulePerm.moduleKey)} />
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Linhas de abas */}
                                    {modulePerm.tabs.map(tabPerm => (
                                        <tr key={`${modulePerm.moduleKey}-${tabPerm.tabKey}`}>
                                            <td style={{ paddingLeft: '32px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{
                                                        width: '1px', height: '14px',
                                                        backgroundColor: '#e2e8f0', flexShrink: 0,
                                                    }} />
                                                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                        {tabPerm.tabTitle}
                                                    </span>
                                                </div>
                                            </td>
                                            {perfis.map(perfil => (
                                                <td key={`${modulePerm.moduleKey}-${tabPerm.tabKey}-${perfil.id}`} style={{ textAlign: 'center' }}>
                                                    <AccessCell hasAccess={checkAccess(perfil, modulePerm.moduleKey, tabPerm.tabKey)} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ════════════ MODAL: CRIAR / EDITAR PERFIL ════════════ */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
                <Modal.Header style={{ padding: 0, border: 'none' }}>
                    <div style={{
                        width: '100%', padding: '20px 24px',
                        background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                        display: 'flex', alignItems: 'center', gap: '12px',
                    }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <Icon d={isEditing ? ICONS.edit : ICONS.plus} size={18} color="#fff" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fff' }}>
                                {isEditing ? 'Editar Perfil' : 'Novo Perfil Customizado'}
                            </p>
                            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                                {isEditing ? 'Altere nome, descrição e permissões' : 'Defina nome, descrição e permissões do novo perfil'}
                            </p>
                        </div>
                        <button
                            onClick={handleCloseModal}
                            style={{
                                width: '28px', height: '28px', borderRadius: '6px',
                                border: '1px solid rgba(255,255,255,0.3)',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                color: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '18px', lineHeight: 1, flexShrink: 0,
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                        >×</button>
                    </div>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px', backgroundColor: '#f9fafb' }}>
                    <form onSubmit={handleSubmit}>
                        {/* Nome */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>
                                Nome do Perfil *
                            </label>
                            <input
                                type="text" name="nome" value={currentPerfil.nome}
                                onChange={e => setCurrentPerfil({ ...currentPerfil, nome: e.target.value })}
                                required
                                placeholder="Ex: Auxiliar Administrativo"
                                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box' }}
                                onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                            />
                        </div>

                        {/* Descrição */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>
                                Descrição
                            </label>
                            <textarea
                                name="descricao" rows={2} value={currentPerfil.descricao}
                                onChange={e => setCurrentPerfil({ ...currentPerfil, descricao: e.target.value })}
                                placeholder="Descreva brevemente as responsabilidades deste perfil..."
                                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff', resize: 'vertical', boxSizing: 'border-box' }}
                                onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
                                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
                            />
                        </div>

                        {/* Permissões */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                <Icon d={ICONS.shield} size={14} color="#2563eb" />
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Permissões de Acesso
                                </span>
                            </div>

                            <div style={{
                                maxHeight: '300px', overflowY: 'auto',
                                border: '1px solid #e5e7eb', borderRadius: '10px',
                                backgroundColor: '#fff', padding: '4px 0',
                            }}>
                                {structuredPermissions.map((modulePerm, idx) => {
                                    const moduleChecked = currentPerfil.permissoes?.modulos?.includes(modulePerm.moduleKey) || false;
                                    return (
                                        <div key={modulePerm.moduleKey} style={{
                                            borderBottom: idx < structuredPermissions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                            padding: '10px 14px',
                                        }}>
                                            {/* Módulo */}
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: modulePerm.tabs.length > 0 ? '8px' : 0 }}>
                                                <input
                                                    type="checkbox"
                                                    className="perm-checkbox"
                                                    checked={moduleChecked}
                                                    onChange={e => handlePermissaoModuloChange(modulePerm.moduleKey, e.target.checked)}
                                                />
                                                <div style={{
                                                    width: '6px', height: '6px', borderRadius: '50%',
                                                    backgroundColor: moduleChecked ? '#2563eb' : '#d1d5db',
                                                    flexShrink: 0, transition: 'background 0.15s',
                                                }} />
                                                <span style={{ fontSize: '13px', fontWeight: '700', color: moduleChecked ? '#111827' : '#6b7280' }}>
                                                    {modulePerm.moduleTitle}
                                                </span>
                                                <span style={{
                                                    fontSize: '10px', color: '#9ca3af',
                                                    backgroundColor: '#f1f5f9', padding: '1px 6px', borderRadius: '4px',
                                                }}>módulo</span>
                                            </label>

                                            {/* Abas */}
                                            {modulePerm.tabs.length > 0 && (
                                                <div style={{ marginLeft: '28px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {modulePerm.tabs.map(tabPerm => {
                                                        const tabChecked = moduleChecked && (currentPerfil.permissoes?.abas?.[modulePerm.moduleKey]?.includes(tabPerm.tabKey) || false);
                                                        return (
                                                            <label key={tabPerm.tabKey} style={{
                                                                display: 'flex', alignItems: 'center', gap: '8px',
                                                                cursor: moduleChecked ? 'pointer' : 'not-allowed',
                                                                opacity: moduleChecked ? 1 : 0.45,
                                                            }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="perm-checkbox"
                                                                    checked={tabChecked}
                                                                    onChange={e => handlePermissaoAbaChange(modulePerm.moduleKey, tabPerm.tabKey, e.target.checked)}
                                                                    disabled={!moduleChecked}
                                                                />
                                                                <span style={{ fontSize: '12px', color: tabChecked ? '#374151' : '#9ca3af' }}>
                                                                    {tabPerm.tabTitle}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Botões */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                            <button type="button" onClick={handleCloseModal} disabled={isLoading}
                                style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={isLoading}
                                style={{
                                    padding: '9px 20px', borderRadius: '8px', border: 'none',
                                    background: isLoading ? '#93c5fd' : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                                    color: '#fff', cursor: isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '13px', fontWeight: '600', fontFamily: 'inherit',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                                }}>
                                {isLoading && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
                                {isLoading ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Perfil'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* ════════════ MODAL: CONFIRMAR EXCLUSÃO ════════════ */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                <Modal.Header style={{ padding: 0, border: 'none' }}>
                    <div style={{
                        width: '100%', padding: '20px 24px',
                        background: 'linear-gradient(135deg, #7f1d1d, #dc2626)',
                        display: 'flex', alignItems: 'center', gap: '12px',
                    }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon d={ICONS.trash} size={18} color="#fff" />
                        </div>
                        <div style={{ flex: 1, fontSize: '15px', fontWeight: '700', color: '#fff' }}>Confirmar Exclusão</div>
                        <button onClick={handleCloseDeleteModal}
                            style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', lineHeight: 1 }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                        >×</button>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
                    <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 6px' }}>
                        Tem certeza que deseja excluir o perfil <strong>"{perfilParaExcluir?.nome}"</strong>?
                    </p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>
                        Esta ação não pode ser desfeita. Colaboradores com este perfil perderão os acessos associados.
                    </p>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
                    <button onClick={handleCloseDeleteModal}
                        style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' }}>
                        Cancelar
                    </button>
                    <button onClick={handleConfirmDelete} disabled={isLoading}
                        style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isLoading ? '#fca5a5' : 'linear-gradient(135deg, #7f1d1d, #dc2626)', color: '#fff', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(220,38,38,0.3)' }}>
                        {isLoading && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
                        {isLoading ? 'Excluindo...' : 'Excluir Perfil'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PerfisTab;
